/**
 * @author      Michael Hettmer <mail@michael-hettmer.de>
 * @copyright   2019 Plusbyte UG (haftungsbeschränkt)
 * @license     {@link https://plusbyte.de Plusbyte License}
 */

import React, { memo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuid } from 'uuid';

import { useFirebaseAnalytics } from '~/apis/useFirebase';
import {
  horseColorIndex,
  PathColorValues,
  selectCurrentState,
  selectHorses,
  selectIsHistoryMode,
  selectMoves,
  selectPlayerId,
  selectSelectedDicesLocal,
  selectTurnDices,
  selectTurnDicesPlayerId,
  selectTurnOrder,
  selectTurnPos,
  selectTurnType,
} from '~/app';
import {
  sendSelectCardDice,
  sendSelectDice,
  sendSelectMove,
  setSelectedDicesLocal,
} from '~/overlay/actions';
import { ReactComponent as ButtonSuccess } from '~/overlay/button-icons/button-success.svg';
import Turfmaster from '~/server/src/tmmodel';
import { cn } from '~/utils/styles';

import AnimatedDice from './AnimatedDice';

export interface TurnControlsProps {
  onCardMoveSelected: (index: number) => void;
  isDiceShuffling: boolean;
  /** When true, we are in SELECTDICES phase (show dice area to all players, including when turnDices not yet populated). */
  isDiceSelection?: boolean;
  /** When false, hide turn controls. Derived in Overlay from phase and acting player (dice vs card). Default true. */
  shouldShowTurnControls?: boolean;
}

const TurnControls = ({
  onCardMoveSelected,
  isDiceShuffling,
  isDiceSelection = false,
  shouldShowTurnControls = true,
}: TurnControlsProps) => {
  const analytics = useFirebaseAnalytics();
  const dispatch = useDispatch();

  const selectedDicesLocal = useSelector(selectSelectedDicesLocal);
  const currentState = useSelector(selectCurrentState);
  const turnType = useSelector(selectTurnType);
  const turnDices = useSelector(selectTurnDices);
  const turnDicesPlayerId = useSelector(selectTurnDicesPlayerId);
  const turnOrder = useSelector(selectTurnOrder);
  const turnPos = useSelector(selectTurnPos);
  const moves = useSelector(selectMoves);
  const playerId = useSelector(selectPlayerId);
  const horses = useSelector(selectHorses);
  const isHistoryMode = useSelector(selectIsHistoryMode);
  const turnDicesPlayerHorses = horses.filter((horse) => horse.playerId === turnDicesPlayerId);

  const currentHorseIndex = turnOrder[turnPos];
  const currentHorse = horses[currentHorseIndex];
  const currentHorsePlayerId = currentHorse ? currentHorse.playerId : -1;

  // we are the dice selecting player
  const isDiceSelectionPlayer =
    turnDices &&
    turnDices.length > 0 &&
    turnType === Turfmaster.turntypes.DICES &&
    playerId === turnDicesPlayerId &&
    currentState === Turfmaster.stateMachine.SELECTDICES;

  // we have to select a single dice because our handicap is too high
  const isDiceSelectionHandi =
    turnDices &&
    turnDices.length > 0 &&
    turnType === Turfmaster.turntypes.DICES &&
    playerId === currentHorsePlayerId &&
    currentState === Turfmaster.stateMachine.SELECTCARDDICE;

  // somebody is selecting a dice
  const isDiceSelectionVisible =
    turnDices &&
    turnDices.length > 0 &&
    turnType === Turfmaster.turntypes.DICES &&
    (currentState === Turfmaster.stateMachine.SELECTDICES || isDiceSelectionHandi);

  // dices pulsating: derive from selection state (no setState in effect)
  const isDicePulsating = (isDiceSelectionPlayer || isDiceSelectionHandi) && selectedDicesLocal < 0;

  const diceColor =
    turnDicesPlayerHorses && turnDicesPlayerHorses.length > 0
      ? turnDicesPlayerHorses[0].color
      : horseColorIndex.BLACK;

  const isDiceSelected = (index: number): boolean => {
    // selecting dice, check against local selection
    if (isDiceSelectionPlayer) return selectedDicesLocal === 2 || selectedDicesLocal === index;
    // selecting dice because of too high handicap, check against local selection
    else if (isDiceSelectionHandi) return selectedDicesLocal === 2 || selectedDicesLocal === index;
    // don't show opponent selection
    else return false;
  };

  /**
   * User clicked on dice in the turn controls, determine if dice should be unselected or selected
   * @param index the clicked dice index (0 or 1)
   */
  const onDiceSelected = (index: number) => {
    if (isHistoryMode) return;
    if (isDiceShuffling) return;
    if (isDiceSelectionPlayer) {
      // no selection, just set index
      if (selectedDicesLocal < 0) dispatch(setSelectedDicesLocal(index));
      // we are selected alone
      else if (selectedDicesLocal < 2 && selectedDicesLocal === index)
        dispatch(setSelectedDicesLocal(-1));
      // other is selected alone
      else if (selectedDicesLocal < 2 && selectedDicesLocal !== index)
        dispatch(setSelectedDicesLocal(2));
      // both are selected, we don't want to be anymore
      else dispatch(setSelectedDicesLocal(index === 0 ? 1 : 0));
    } else if (isDiceSelectionHandi) {
      if (selectedDicesLocal === index) dispatch(setSelectedDicesLocal(-1));
      else dispatch(setSelectedDicesLocal(index));
    } else dispatch(setSelectedDicesLocal(-1));
  };

  /**
   * If we are the dice selecting player and a dice selection got accepted in the turn controls tell the controller
   * @param index index of the selected dice
   */
  const onAccept = () => {
    if (isHistoryMode) return;
    if (selectedDicesLocal >= 0) {
      if (isDiceSelectionPlayer) {
        dispatch(sendSelectDice({ playerid: playerId, dicesid: selectedDicesLocal }));
        dispatch(setSelectedDicesLocal(-1));
      } else if (isDiceSelectionHandi)
        dispatch(
          sendSelectCardDice({
            playerid: playerId,
            horseid: currentHorseIndex,
            carddiceid: selectedDicesLocal,
          }),
        );
    }
  };

  // if we are not selecting a dice and we are the current player and there are moves available then show them
  const isMovesVisible =
    !isDiceSelectionVisible && moves && moves.length > 0 && currentHorsePlayerId === playerId;

  const onDiceMoveSelected = useCallback(
    (index: number) => {
      if (isHistoryMode) return;
      dispatch(
        sendSelectMove({
          playerid: playerId,
          horseid: currentHorseIndex,
          carddiceid: selectedDicesLocal,
          moveid: index,
        }),
      );
      dispatch(setSelectedDicesLocal(-1));
    },
    [currentHorseIndex, dispatch, playerId, selectedDicesLocal, isHistoryMode],
  );

  const onMoveSelected = useCallback(
    (index: number) => {
      if (turnType === Turfmaster.turntypes.CARDS) {
        onCardMoveSelected(index);
      } else {
        onDiceMoveSelected(index);
      }
      analytics?.logEvent('select_move_from_right_bar', { turnType });
    },
    [analytics, onCardMoveSelected, onDiceMoveSelected, turnType],
  );

  // hide content if we are not the current horse in a card round
  if (
    turnType === Turfmaster.turntypes.CARDS &&
    (!currentHorse || currentHorse.playerId !== playerId)
  )
    return null;

  // Hide controls when Overlay determined we should not show, except during dice shuffle or dice result (SELECTDICESOPP).
  if (!shouldShowTurnControls && !isDiceShuffling && !isDiceSelection) return null;

  const showDiceBlock = isDiceSelectionVisible || isDiceShuffling || isDiceSelection;
  const diceValues = turnDices && turnDices.length > 0 ? turnDices : [1, 1];

  return (
    <div className="pointer-events-auto max-h-[15vh] px-4 py-[2vh]">
      {showDiceBlock && (
        <div className="grid min-h-[11vh] auto-cols-[10vh] grid-flow-col place-content-center items-center justify-items-center gap-x-4">
          {diceValues.map((dice, index) => (
            <AnimatedDice
              key={index}
              className={cn('h-[10vh] w-[10vh] transition-all duration-150', 'cursor-not-allowed', {
                'cursor-pointer active:scale-90':
                  !isDiceShuffling &&
                  !isHistoryMode &&
                  (isDiceSelectionPlayer || isDiceSelectionHandi),
                'opacity-50':
                  !isDiceShuffling &&
                  (isHistoryMode ||
                    ((isDiceSelectionPlayer || isDiceSelectionHandi) && !isDiceSelected(index))),
                'opacity-100':
                  !isDiceShuffling &&
                  !isHistoryMode &&
                  (isDiceSelectionPlayer || isDiceSelectionHandi) &&
                  isDiceSelected(index),
                'origin-center animate-[diceShuffle_0.5s_linear_infinite]': isDiceShuffling,
                'origin-center animate-[dicePulse_3.8s_ease-in-out_2s_infinite]': isDicePulsating,
              })}
              value={dice}
              color={diceColor}
              isShuffling={isDiceShuffling}
              isPulsating={isDicePulsating}
              onClick={() => onDiceSelected(index)}
            />
          ))}
          {!isDiceShuffling &&
            !isHistoryMode &&
            (isDiceSelectionPlayer || isDiceSelectionHandi) && (
              <ButtonSuccess
                key="buttonSuccess"
                className={cn(
                  'h-[8vh] w-[8vh] transition-all duration-150',
                  'animate-[bounceInAcceptButton_0.5s_backwards]',
                  'active:scale-90',
                  selectedDicesLocal >= 0 ? 'cursor-pointer opacity-100' : 'opacity-50',
                )}
                onClick={onAccept}
                onKeyDown={(e) => e.key === 'Enter' && onAccept}
                role="button"
                tabIndex={0}
              />
            )}
        </div>
      )}

      <div
        className={cn(
          'grid min-h-[11vh] grid-cols-[repeat(auto-fit,minmax(5vh,calc(100%/6)))] place-content-center items-center justify-items-center gap-x-2 gap-y-[1vh]',
          { 'xl:grid-cols-[repeat(auto-fit,minmax(8vh,calc(100%/6)))]': moves.length < 6 },
        )}
      >
        {!isDiceShuffling &&
          isMovesVisible &&
          moves.map((_, index) => (
            <div
              key={uuid()}
              className={cn(
                'rounded-full transition-all duration-150',
                'animate-[bounceInMoves_0.5s_backwards]',
                'h-[5vh] w-[5vh]',
                { 'xl:h-[8vh] xl:w-[8vh]': moves.length < 6 },
                isHistoryMode ? 'cursor-not-allowed opacity-50' : 'cursor-pointer active:scale-90',
              )}
              style={{ backgroundColor: PathColorValues[index] }}
              onClick={() => onMoveSelected(index)}
              onKeyDown={(e) => e.key === 'Enter' && onMoveSelected(index)}
              role="button"
              tabIndex={0}
            />
          ))}
      </div>
    </div>
  );
};

export default memo(TurnControls);
